import { useState } from 'react';

import { twoFaEmailOtpVerify, twoFaEmailPasswordVerify } from 'actions/other';
import StatusIconContent from 'components/common/StatusIconContent';
import { errorHasACode } from 'helpers/error';
import { getBlockedTime } from 'helpers/numbers';
import useApi from 'hooks/useApi';
import i18n from 'i18n/config';
import { selectDisableFaceAuthEmailPass } from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { TwoFactorDisableOtpVerificationResponse } from 'types/actions/other';
import { ErrorMap } from 'types/utils';
import Box from 'UIKit/Box';
import DeltaOTPVerification, {
  DeltaOTPVerification as DeltaOTPVerificationProps,
} from 'UIKit/DeltaOTPVerification';

import {
  OTP_VERIFICATION_ERROR_CODES,
  DISABLE_2FA_FLOW,
} from './Disable2faFaceAuth.constants';
import styles from './disable2faFaceAuth.module.scss';
import { WorkflowData } from './Disable2faFaceAuth.types';

const textEmailVerification = i18n.t('auth:emailVerification');

const textSelfieNotClear = i18n.t<string>('errors:selfieNotClearRaiseTicketDisable2fa');

const ERROR_MESSAGE_MAPPING: ErrorMap<
  OTP_VERIFICATION_ERROR_CODES,
  {
    [OTP_VERIFICATION_ERROR_CODES.INVALID_OTP]: {
      max_attempts: number;
      count: number;
    };
    [OTP_VERIFICATION_ERROR_CODES.BLOCKED]: {
      time: number;
    };
  },
  { showInOtpPanel?: boolean; primaryText: string; secondaryText?: string }
> = {
  [OTP_VERIFICATION_ERROR_CODES.INVALID_OTP]: ctx => {
    return {
      showInOtpPanel: true,
      primaryText: i18n.t('errors:custom.invalid_email_otp', {
        count: ctx.max_attempts - ctx.count,
      }),
    };
  },
  [OTP_VERIFICATION_ERROR_CODES.BLOCKED]: ctx => {
    const { unit, value } = getBlockedTime(ctx.time);

    return {
      showInOtpPanel: true,
      primaryText: i18n.t(`errors:user.emailBlockedText_${unit}`, {
        timeValue: value,
        count: value,
      }),
    };
  },
};

const getDisplayError = (
  error: unknown
): { showInOtpPanel?: boolean; primaryText?: string; secondaryText?: string } | null => {
  if (!error) return null;

  if (errorHasACode(error)) {
    if (typeof ERROR_MESSAGE_MAPPING[error.code] === 'function') {
      return ERROR_MESSAGE_MAPPING[error.code](error.context);
    }
    return (
      ERROR_MESSAGE_MAPPING[error.code] ?? {
        primaryText: i18n.t('errors:utils.somethingWentWrong'),
      }
    );
  }
  return {
    primaryText: i18n.t('errors:utils.somethingWentWrong'),
  };
};

const EmailOtpVerification = ({
  updateWorkflowData,
  updateActiveFlow,
}: {
  updateWorkflowData: (data: WorkflowData | null) => void;
  updateActiveFlow: (flow: DISABLE_2FA_FLOW, options?: { message?: string }) => void;
}) => {
  const { requestedEmail, requestedPasswordBase64 } = useAppSelector(
    selectDisableFaceAuthEmailPass
  );

  const appDispatch = useAppDispatch();

  const [resend, setResendCount] = useState(0);

  const {
    error: verificationError,
    isLoading,
    requestor,
    reset,
  } = useApi<any, TwoFactorDisableOtpVerificationResponse['result'], any>({
    requestFn: (otp: string) =>
      appDispatch(twoFaEmailOtpVerify({ data: { otp, email: requestedEmail! } })),
    onSuccess: data => {
      updateWorkflowData({
        authToken: data.auth_token,
        workflowId: data.faceAuth_workflow_id,
        transactionId: data.transaction_id,
        vendor: data.vendor,
        uid: data.uid,
        recoveryId: data.recovery_id,
      });

      updateActiveFlow(DISABLE_2FA_FLOW.FACE_AUTH);
    },
    onError: error => {
      if (
        [
          OTP_VERIFICATION_ERROR_CODES.HYPERVERGE_ACCOUNT_ENROL_FAILED,
          OTP_VERIFICATION_ERROR_CODES.FAILED_TO_ENROL_DATA_FROM_HYPERVERGE,
          OTP_VERIFICATION_ERROR_CODES.S3_ERROR,
        ].includes(error.code)
      ) {
        updateActiveFlow(DISABLE_2FA_FLOW.RAISE_TICKET, {
          message: textSelfieNotClear,
        });
      }
    },
  });

  const { requestor: resendOtpRequestor, error: resendError } = useApi({
    requestFn: () =>
      appDispatch(
        twoFaEmailPasswordVerify({
          data: {
            email: requestedEmail!,
            password: atob(requestedPasswordBase64!),
          },
        })
      ),
    onSuccess: () => {
      setResendCount(resend + 1);
    },
  });

  const displayError = getDisplayError(verificationError || resendError);

  const showOtpPanel = !displayError || displayError.showInOtpPanel;

  const handleConfirm: DeltaOTPVerificationProps['onConfirm'] = ({ emailCode }) => {
    reset();
    requestor(emailCode);
  };

  return (
    (<Box>
      <Box as="h2" className={styles.twofaHeading} renderWhen={!displayError}>
        {textEmailVerification}
      </Box>
      <Box className={styles.otpVerification}>
        {showOtpPanel ? (
          <DeltaOTPVerification
            onConfirm={handleConfirm}
            variant="panel"
            hideResendEmailCode={resend > 0}
            showMfa={false}
            isLoading={isLoading}
            onResendOtp={resendOtpRequestor}
            errors={{ emailCode: displayError?.primaryText }}
          />
        ) : (
          <StatusIconContent
            testId="2fa-disable-otp-error"
            icon={<StatusIconContent.Icon status="error" data-palette="EmailOtpVerification" />}
            heading={displayError?.primaryText ?? ''}
            message={displayError?.secondaryText}
          />
        )}
      </Box>
    </Box>)
  );
};

export default EmailOtpVerification;
